import { RouterOutlet } from "@angular/router";
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Inject,
  inject,
  NgZone,
  OnInit,
  PLATFORM_ID,
} from "@angular/core";
import { defineCustomElement as defineNesButtonElement } from "@nessie/webcomponents/dist/components/nes-button";
import { defineCustomElement as defineNesInputElement } from "@nessie/webcomponents/dist/components/nes-input";
import { defineCustomElement as defineNesLinkElement } from "@nessie/webcomponents/dist/components/nes-link";
import { CommonModule, isPlatformBrowser } from "@angular/common";
import { TranslateService } from "@ngx-translate/core";
import { SpinnerComponent } from "./shared/icons/spinner/spinner.component";
import { environment } from './../environments/environment';
import { GigyaService } from "./services/gigya.service";
import { Version, VersionService } from "./services/version.service";

type GigyaConfig = {
  apiDomain?: string;
  oidc: Record<string, never>;
};

type GigyaOidcScriptConfig = {
  errorURL: string;
  consentURL: string;
};

defineNesButtonElement();
defineNesInputElement();
defineNesLinkElement();

@Component({
  selector: "app-root",
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    SpinnerComponent,
  ],
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.css",
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppComponent implements OnInit {
  title = "NS";
  public loading = true;

  private translate = inject(TranslateService);

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private ngZone: NgZone,
    private gigyaService: GigyaService,
    private versionService: VersionService,
  ) {
    this.translate.addLangs(["nl", "en"]);
    this.translate.setFallbackLang("nl");
    this.translate.use("nl");
  }

  public ngOnInit(): void {
    console.info("AppComponent.ngOnInit - platform:", isPlatformBrowser(this.platformId) ? "browser" : "server", "userAgent:", typeof navigator !== "undefined" ? navigator.userAgent : "n/a");
    if (isPlatformBrowser(this.platformId) && typeof window !== "undefined") {
      const searchParams = new URLSearchParams(window.location.search);

      // Opt-in only (?navDiag=true) - these patch global function references
      // (window.open, location.assign/replace, etc.), which would make them
      // fail a `fn.toString().includes('[native code]')` check. Gigya's RBA
      // (risk-based auth) or other anti-tamper logic could use exactly that
      // kind of check, so this must stay inert for normal traffic and only
      // run when explicitly requested for a debugging session.
      if (searchParams.get("navDiag") !== null) {
        this.logStorageAvailability();
        this.installNavigationDiagnostics();
      }

      const gigyaConf = this.getGigyaConfig(window.location.hostname);
      console.info('Use gigya conf', gigyaConf, 'for hostname', window.location.hostname);
      (window as any).__gigyaConf = gigyaConf;
      const oidcEnabled = !!searchParams.get("oidcEnabled") || this.versionService.nativeAppOidc;
      if (this.versionService.nativeAppOidc) {
        console.info("AppComponent.ngOnInit - native app OIDC client detected, loading gigya.oidc.js immediately (no oidcEnabled reload needed).");
      }
      console.info("Full location on app init:", window.location.href, "| oidcEnabled:", oidcEnabled);

      (window as any).onGigyaServiceReady = () => {
        this.ngZone.run(() => {
          console.info("gigya ready (onGigyaServiceReady fired)");
          this.loading = false;

          if (oidcEnabled) {
            console.info("Registering onOidc event handler (oidcEnabled=true)");
            // Required by Gigya's "Login Page as Proxy for OIDC OP" contract - without a
            // registered onOidc handler the SDK may not complete a pending OIDC transaction
            // (e.g. one that requires consent) once the user already has a valid session.
            this.gigyaService.client.accounts.addEventHandlers({
              onOidc: (params: any) => {
                this.ngZone.run(() => console.info("onOidc event fired:", params));
              },
            });
          } else {
            console.info("oidcEnabled=false - skipping onOidc handler registration");
          }
        });
      };

      if (searchParams.get("lang") === "en" || searchParams.get("language") === "en") {
        console.info("Language override detected in URL - using 'en'");
        this.translate.use("en");
      } else {
        console.info("No language override in URL - using default 'nl'");
      }

      const baseUrl = gigyaConf.apiDomain ?? "https://cdns.gigya.com";
      if (!gigyaConf.apiDomain) {
        console.warn("No apiDomain resolved for hostname", window.location.hostname, "- falling back to default", baseUrl);
      }
      const apiKey = this.versionService.current === Version.NSGO
        ? environment.nsgoApiKey
        : environment.apiKey;
      console.info("Resolved gigya version/apiKey selection - version:", this.versionService.current);

      if (oidcEnabled) {
        console.info("prepare gigya with OIDC - baseUrl:", baseUrl);
        this.injectGigyaSdk(
          `${baseUrl}/JS/gigya.oidc.js?apiKey=${apiKey}`,
          {
            errorURL: `${window.location.origin}/continue?oidcStep=error`,
            consentURL: `${window.location.origin}/continue?oidcStep=consent`,
          },
        );
      } else {
        console.info("prepare gigya without OIDC - baseUrl:", baseUrl);
        this.injectGigyaSdk(
          `${baseUrl}/JS/gigya.js?apiKey=${apiKey}`,
        );
      }
    } else {
      console.warn("AppComponent.ngOnInit skipped gigya bootstrap - not running in a browser platform");
    }
  }

  /**
   * TEMPORARY DIAGNOSTIC - remove once the mobile OIDC-redirect-not-happening
   * issue is root-caused. Checks whether localStorage/sessionStorage are
   * actually usable - some Android WebView configurations expose the objects
   * but throw (or don't persist) on read/write, which would silently break
   * anything (including gigya's own SDK) that relies on them to stash OIDC
   * state (nonce/PKCE verifier/redirect target) between steps.
   */
  private logStorageAvailability(): void {
    for (const [name, storage] of [["localStorage", (() => { try { return window.localStorage; } catch { return null; } })()], ["sessionStorage", (() => { try { return window.sessionStorage; } catch { return null; } })()]] as const) {
      if (!storage) {
        console.warn(`Storage diagnostic - window.${name} is null/inaccessible in this environment.`);
        continue;
      }
      try {
        const probeKey = "__storage_probe__";
        storage.setItem(probeKey, "1");
        storage.removeItem(probeKey);
        console.info(`Storage diagnostic - window.${name} is available and writable.`);
      } catch (err) {
        console.warn(`Storage diagnostic - window.${name} exists but threw on write:`, err);
      }
    }
  }

  /**
   * TEMPORARY DIAGNOSTIC - remove once the mobile OIDC-redirect-not-happening
   * issue is root-caused. Patches the common browser navigation entry points
   * so we can see exactly what URL (if any) gigya's SDK attempts to navigate
   * to during the OIDC completion step, since that step currently fails
   * silently with no console error on affected mobile sessions.
   */
  private installNavigationDiagnostics(): void {
    console.info("[nav-diag] installing temporary navigation diagnostics");

    try {
      const originalAssign = Location.prototype.assign;
      Location.prototype.assign = function (this: Location, url: string | URL) {
        console.warn("[nav-diag] location.assign() called with:", url);
        return originalAssign.call(this, url as string);
      };
    } catch (err) {
      console.warn("[nav-diag] could not patch Location.prototype.assign:", err);
    }

    try {
      const originalReplace = Location.prototype.replace;
      Location.prototype.replace = function (this: Location, url: string | URL) {
        console.warn("[nav-diag] location.replace() called with:", url);
        return originalReplace.call(this, url as string);
      };
    } catch (err) {
      console.warn("[nav-diag] could not patch Location.prototype.replace:", err);
    }

    try {
      const originalOpen = window.open.bind(window);
      window.open = ((...args: Parameters<typeof window.open>) => {
        console.warn("[nav-diag] window.open() called with:", args[0]);
        return originalOpen(...args);
      }) as typeof window.open;
    } catch (err) {
      console.warn("[nav-diag] could not patch window.open:", err);
    }

    try {
      const originalSubmit = HTMLFormElement.prototype.submit;
      HTMLFormElement.prototype.submit = function (this: HTMLFormElement) {
        console.warn("[nav-diag] form.submit() called, action:", this.action);
        return originalSubmit.call(this);
      };
    } catch (err) {
      console.warn("[nav-diag] could not patch HTMLFormElement.prototype.submit:", err);
    }

    try {
      const originalClick = HTMLAnchorElement.prototype.click;
      HTMLAnchorElement.prototype.click = function (this: HTMLAnchorElement) {
        console.warn("[nav-diag] anchor.click() called, href:", this.href);
        return originalClick.call(this);
      };
    } catch (err) {
      console.warn("[nav-diag] could not patch HTMLAnchorElement.prototype.click:", err);
    }

    // Best-effort only: modern browsers mark Location#href as [Unforgeable]
    // (non-configurable) for security, so this will usually fail - that's
    // expected and logged, not an error condition.
    try {
      const descriptor = Object.getOwnPropertyDescriptor(Location.prototype, "href");
      if (descriptor?.configurable && descriptor.get && descriptor.set) {
        const originalGet = descriptor.get;
        const originalSet = descriptor.set;
        Object.defineProperty(Location.prototype, "href", {
          configurable: true,
          get(this: Location) {
            return originalGet.call(this);
          },
          set(this: Location, url: string) {
            console.warn("[nav-diag] location.href = ... assignment attempted:", url);
            originalSet.call(this, url);
          },
        });
        console.info("[nav-diag] patched Location.prototype.href setter");
      } else {
        console.info("[nav-diag] Location.prototype.href is not configurable in this browser - skipping (assign/replace/open/submit/click patches still active)");
      }
    } catch (err) {
      console.info("[nav-diag] could not patch Location.prototype.href (expected in most browsers):", err);
    }
  }

  private injectGigyaSdk(url: string, oidcConfig?: GigyaOidcScriptConfig): void {
    console.info("Injecting gigya SDK script:", url, oidcConfig ? "(with OIDC config)" : "(no OIDC config)");
    (function (src, config, callback, onError) {
      var script = document.createElement("script");
      script.src = src;
      if (config) {
        script.text = JSON.stringify(config);
      }
      script.onload = callback;
      script.onerror = onError;
      document.head.appendChild(script);
    })(
      url,
      oidcConfig,
      function () {
        console.info("gigya SDK script downloaded successfully:", url);
      },
      function (event: unknown) {
        console.warn("gigya SDK script FAILED to load - this will block the entire login flow:", url, event);
      },
    );
  }

  private getGigyaConfig(hostname: string): GigyaConfig {
    if (this.versionService.current === Version.NSGO) {
      return { apiDomain: environment.gigyaApiDomainNsgo, oidc: {} };
    }

    const apiDomain = environment.gigyaApiDomainsByHostname[hostname];

    if (apiDomain) {
      return { apiDomain, oidc: {} };
    }

    return { oidc: {} };
  }
}
